import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { tenantOverride, useTenants, useUserMutations, useUserQuota, useUsers, type RoleAssignmentInput, type UserDto, type UserFilters } from '../api/admin'
import { useCompanies, useTemplates } from '../api/hooks'
import type { UserRole } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Badge, Empty, Modal, fmtDateTime } from '../components/ui'
import { errMsg } from './TemplatesPage'

const ROLES: UserRole[] = ['TenantAdmin', 'Auditor', 'Viewer', 'Supplier']

interface Draft { id?: string; email: string; fullName: string; password: string; phone: string; title: string; isActive: boolean; roles: RoleAssignmentInput[] }
const blank = (): Draft => ({ email: '', fullName: '', password: '', phone: '', title: '', isActive: true, roles: [{ role: 'Auditor' }] })

export default function UsersPage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const set = (k: string, v: string) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); if (k !== 'page') n.delete('page'); setParams(n) }
  const filters: UserFilters & { page: number } = { page: Number(params.get('page') ?? 1), search: params.get('search') ?? '', role: (params.get('role') as UserRole | null) ?? '', includeInactive: params.get('inactive') === '1' }
  const users = useUsers(filters)
  const companies = useCompanies()
  const templates = useTemplates()
  const m = useUserMutations()
  const quota = useUserQuota()
  // The platform admin manages one tenant at a time: users and the quota below belong to the tenant chosen here
  // (the same choice as "Kiracılar → Geçiş yap"); without one the API has no tenant to put a user into.
  const { user: me } = useAuth()
  const isSuper = me?.roles.some((r) => r.role === 'SuperAdmin') ?? false
  const tenants = useTenants(isSuper)
  const activeTenant = tenantOverride.get() ?? ''
  const switchTenant = (id: string) => { tenantOverride.set(id || null); window.location.reload() }
  const [draft, setDraft] = useState<Draft | null>(null)
  const [reset, setReset] = useState<{ user: UserDto; password: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  const save = async () => {
    if (!draft) return
    setError(null)
    try {
      if (draft.id) await m.update.mutateAsync({ id: draft.id, fullName: draft.fullName, phone: draft.phone || undefined, title: draft.title || undefined, isActive: draft.isActive, roles: draft.roles })
      else await m.create.mutateAsync({ email: draft.email, fullName: draft.fullName, password: draft.password, phone: draft.phone || undefined, title: draft.title || undefined, roles: draft.roles })
      setDraft(null)
    } catch (e) {
      setError(errMsg(e))
    }
  }
  const setRole = (i: number, patch: Partial<RoleAssignmentInput>) => draft && setDraft({ ...draft, roles: draft.roles.map((r, j) => (j === i ? { ...r, ...patch } : r)) })
  const scopeName = (r: RoleAssignmentInput) => {
    const parts: string[] = []
    if (r.companyId) parts.push(companies.data?.find((c) => c.id === r.companyId)?.name ?? '?')
    if (r.siteId) parts.push(companies.data?.flatMap((c) => c.sites).find((s) => s.id === r.siteId)?.name ?? '?')
    if (r.formTemplateId) parts.push(templates.data?.find((x) => x.id === r.formTemplateId)?.code ?? '?')
    return parts.length ? ` (${parts.join(' / ')})` : ''
  }

  return (
    <div className="stack">
      <div className="row between wrap">
        <h2>{t('users.title')}</h2>
        <button onClick={() => { setError(null); setDraft(blank()) }} disabled={quota.data?.isFull || (isSuper && !activeTenant)}>{t('users.new')}</button>
      </div>
      {isSuper ? (
        <div className="card row between wrap" style={{ borderColor: activeTenant ? undefined : '#e34948' }}>
          <div>
            <b>{t('users.managedTenant')}</b>
            <div className="muted small">{activeTenant ? t('users.managedTenantHint') : t('users.noTenantSelected')}</div>
          </div>
          <select value={activeTenant} onChange={(e) => switchTenant(e.target.value)} aria-label={t('users.managedTenant')}>
            <option value="">{t('users.chooseTenant')}</option>
            {(tenants.data?.items ?? []).map((x) => <option key={x.id} value={x.id}>{x.name} ({x.usedUsers}/{x.maxUsers})</option>)}
          </select>
        </div>
      ) : null}
      {quota.data ? (
        <div className="card row between wrap" style={{ borderColor: quota.data.isFull ? '#e34948' : undefined }}>
          <div>
            <b>{t('users.quota', { used: quota.data.usedUsers, max: quota.data.maxUsers })}</b>
            <div className="muted small">{t('users.quotaHint', { base: quota.data.baseQuota, pack: quota.data.usersPerPack, packs: quota.data.extraUserPacks })}</div>
            {quota.data.isFull ? <div className="error small">{t('users.quotaFull')}</div> : null}
          </div>
          <div className="progress" style={{ width: 160 }}><div style={{ width: `${Math.min(100, (100 * quota.data.usedUsers) / Math.max(1, quota.data.maxUsers))}%`, background: quota.data.isFull ? '#e34948' : undefined }} /></div>
        </div>
      ) : null}
      <div className="card filters">
        <input placeholder={t('common.search')} value={filters.search} onChange={(e) => set('search', e.target.value)} />
        <select value={filters.role} onChange={(e) => set('role', e.target.value)}>
          <option value="">{t('users.allRoles')}</option>
          {ROLES.map((r) => <option key={r} value={r}>{t(`roles.${r}`)}</option>)}
        </select>
        <label className="row"><input type="checkbox" checked={filters.includeInactive} onChange={(e) => set('inactive', e.target.checked ? '1' : '')} /> {t('users.includeInactive')}</label>
      </div>
      {info ? <p className="muted small">{info}</p> : null}
      <div className="card">
        {users.isLoading ? <p>{t('common.loading')}</p> : !users.data?.items.length ? <Empty text={t('users.empty')} /> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>{t('users.name')}</th><th>{t('users.email')}</th><th>{t('users.roles')}</th><th>{t('users.lastLogin')}</th><th>{t('users.status')}</th><th /></tr></thead>
              <tbody>
                {users.data.items.map((u) => (
                  <tr key={u.id}>
                    <td><b>{u.fullName}</b>{u.title ? <div className="muted small">{u.title}</div> : null}</td>
                    <td className="small">{u.email}{u.phone ? <div className="muted">{u.phone}</div> : null}</td>
                    <td className="small">{u.roles.map((r, i) => <div key={i}>{t(`roles.${r.role}`)}{scopeName(r)}</div>)}</td>
                    <td className="small">{u.lastLoginAt ? fmtDateTime(u.lastLoginAt) : '–'}{u.mustChangePassword ? <div className="muted">{t('users.mustChange')}</div> : null}</td>
                    <td>{u.isActive ? <Badge color="#008300">{t('users.active')}</Badge> : <Badge color="#e34948">{t('users.inactive')}</Badge>}</td>
                    <td className="row" style={{ whiteSpace: 'nowrap' }}>
                      <button className="ghost" onClick={() => { setError(null); setDraft({ id: u.id, email: u.email, fullName: u.fullName, password: '', phone: u.phone ?? '', title: u.title ?? '', isActive: u.isActive, roles: u.roles.map((r) => ({ ...r })) }) }}>{t('users.edit')}</button>
                      <button className="ghost" onClick={() => { setError(null); setReset({ user: u, password: '' }) }}>{t('users.resetPassword')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {users.data && users.data.totalPages > 1 ? (
          <div className="row between" style={{ marginTop: 8 }}>
            <button className="secondary small-btn" disabled={filters.page <= 1} onClick={() => set('page', String(filters.page - 1))}>‹</button>
            <span className="muted small">{t('common.pageOf', { page: users.data.page, pages: users.data.totalPages, total: users.data.totalCount })}</span>
            <button className="secondary small-btn" disabled={filters.page >= users.data.totalPages} onClick={() => set('page', String(filters.page + 1))}>›</button>
          </div>
        ) : null}
      </div>

      {draft ? (
        <Modal title={draft.id ? t('users.edit') : t('users.new')} onClose={() => setDraft(null)} width={720}>
          <div className="grid two">
            <label>{t('users.name')}<input value={draft.fullName} onChange={(e) => setDraft({ ...draft, fullName: e.target.value })} /></label>
            <label>{t('users.email')}<input type="text" autoCapitalize="none" value={draft.email} disabled={!!draft.id} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></label>
            {!draft.id ? <label>{t('users.password')}<input type="password" value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} /></label> : null}
            <label>{t('users.phone')}<input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></label>
            <label>{t('users.jobTitle')}<input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></label>
            {draft.id ? <label className="row"><input type="checkbox" checked={draft.isActive} onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} /> {t('users.active')}</label> : null}
          </div>
          <h3>{t('users.roles')}</h3>
          <p className="muted small">{t('users.rolesHint')}</p>
          {draft.roles.map((r, i) => (
            <div key={i} className="row wrap" style={{ alignItems: 'flex-end' }}>
              <label style={{ minWidth: 150 }}>{t('users.role')}
                <select value={r.role} onChange={(e) => setRole(i, { role: e.target.value as UserRole })}>{ROLES.map((x) => <option key={x} value={x}>{t(`roles.${x}`)}</option>)}</select>
              </label>
              <label style={{ minWidth: 180 }}>{t('companies.name')}
                <select value={r.companyId ?? ''} onChange={(e) => setRole(i, { companyId: e.target.value || null, siteId: null })}>
                  <option value="">{r.role === 'Supplier' ? t('users.pickCompany') : t('users.allCompanies')}</option>
                  {companies.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label style={{ minWidth: 150 }}>{t('companies.site')}
                <select value={r.siteId ?? ''} disabled={!r.companyId} onChange={(e) => setRole(i, { siteId: e.target.value || null })}>
                  <option value="">{t('common.all')}</option>
                  {companies.data?.find((c) => c.id === r.companyId)?.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              <label style={{ minWidth: 150 }}>{t('reports.template')}
                <select value={r.formTemplateId ?? ''} onChange={(e) => setRole(i, { formTemplateId: e.target.value || null })}>
                  <option value="">{t('common.all')}</option>
                  {templates.data?.map((x) => <option key={x.id} value={x.id}>{x.code}</option>)}
                </select>
              </label>
              <button className="ghost" style={{ color: 'var(--danger)' }} disabled={draft.roles.length === 1} onClick={() => setDraft({ ...draft, roles: draft.roles.filter((_, j) => j !== i) })}>✕</button>
            </div>
          ))}
          <button className="secondary small-btn" style={{ alignSelf: 'flex-start' }} onClick={() => setDraft({ ...draft, roles: [...draft.roles, { role: 'Viewer' }] })}>{t('users.addRole')}</button>
          {error ? <p className="error">{error}</p> : null}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="secondary" onClick={() => setDraft(null)}>{t('common.cancel')}</button>
            <button disabled={!draft.fullName || !draft.email || (!draft.id && draft.password.length < 8) || m.create.isPending || m.update.isPending} onClick={() => void save()}>{t('common.save')}</button>
          </div>
        </Modal>
      ) : null}

      {reset ? (
        <Modal title={`${t('users.resetPassword')}: ${reset.user.fullName}`} onClose={() => setReset(null)}>
          <label>{t('users.newPassword')}<input type="password" value={reset.password} onChange={(e) => setReset({ ...reset, password: e.target.value })} /></label>
          <p className="muted small">{t('users.resetHint')}</p>
          {error ? <p className="error">{error}</p> : null}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="secondary" onClick={() => setReset(null)}>{t('common.cancel')}</button>
            <button disabled={reset.password.length < 8} onClick={() => { setError(null); m.resetPassword.mutateAsync({ id: reset.user.id, newPassword: reset.password }).then(() => { setReset(null); setInfo(t('users.resetDone', { name: reset.user.fullName })) }).catch((e: unknown) => setError(errMsg(e))) }}>{t('common.save')}</button>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}
